import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { endpoints } from "../api.js";
import { useErrorMessage, useLocale } from "../i18n/hooks.js";
import { Icon } from "./Icon.jsx";
import { ErrorBanner, Modal, Spinner } from "./ui.jsx";

export const REPORT_FORMATS = ["html", "json", "pdf"];

export async function openReport(reportId) {
  const { url } = await endpoints.reportUrl(reportId);
  window.open(url, "_blank", "noopener,noreferrer");
}

/** Generate a report for one job of an investigation. */
export function ReportDialog({ investigationId, jobId, onClose, onCreated, findingCount }) {
  const intl = useIntl();
  const locale = useLocale();
  const errMsg = useErrorMessage();
  const [format, setFormat] = useState("pdf");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const generate = async () => {
    setBusy(true);
    setError(null);
    try {
      const { report } = await endpoints.createReport({ investigationId, jobId, format, locale });
      onCreated?.(report);
      onClose();
    } catch (e) {
      setError(e);
      setBusy(false);
    }
  };

  return (
    <Modal
      title={intl.formatMessage({ id: "reports.generateTitle" })}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            <FormattedMessage id="common.cancel" />
          </button>
          <button type="button" className="btn btn-primary" onClick={generate} disabled={busy}>
            {busy ? <Spinner size={16} /> : <Icon name="file" size={15} />}
            <FormattedMessage id="reports.generate" />
          </button>
        </>
      }
    >
      {findingCount ? (
        <p className="muted small">
          <FormattedMessage id="reports.selectionNote" values={{ count: findingCount }} />
        </p>
      ) : null}
      <div className="seg seg-lg" role="radiogroup">
        {REPORT_FORMATS.map((f) => (
          <button
            key={f}
            type="button"
            role="radio"
            aria-checked={format === f}
            className={`seg-btn ${format === f ? "is-active" : ""}`}
            onClick={() => setFormat(f)}
          >
            <FormattedMessage id={`reports.format.${f}`} />
          </button>
        ))}
      </div>
      <ErrorBanner message={error ? errMsg(error) : null} />
    </Modal>
  );
}
